import React, { useState, useEffect } from 'react';
import { API, getToken } from './adminApi';

// The AI chat landing pages (/meet/:communityRef): funnel, drop-off, v1 vs
// v2, by community and ad source, and each conversation — click a row to see
// how far they got and the whole chat. Shown at the bottom of Page Analytics,
// using that tab's filters.

const fmtDuration = (ms) => {
  if (!ms) return '0s';
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, '0')}s`;
  return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`;
};
const fmtTime = (d) => new Date(d).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });

const StatTile = ({ label, value, sub }) => (
  <div className="as-pa-tile">
    <div className="as-pa-tile-label">{label}</div>
    <div className="as-pa-tile-value">{value}</div>
    {sub && <div className="as-pa-tile-sub">{sub}</div>}
  </div>
);

const GroupTable = ({ title, rows }) => (
  <>
    <h2 className="as-section-title" style={{ marginTop: '1rem' }}>{title}</h2>
    <div className="as-act-table-wrap">
    <table className="as-act-table">
      <thead><tr><th></th><th>Chats</th><th>Replied</th><th>Saw people</th><th>Gave email</th><th>Accounts</th></tr></thead>
      <tbody>
        {rows.length === 0 && <tr><td colSpan="6" className="as-act-empty">—</td></tr>}
        {rows.map(r => (
          <tr key={r.key}>
            <td>{r.key}</td>
            <td className="as-pa-num">{r.conversations}</td>
            <td className="as-pa-num">{r.chatted}%</td>
            <td className="as-pa-num">{r.sawPeople}%</td>
            <td className="as-pa-num">{r.gaveEmail}%</td>
            <td className="as-pa-num">{r.accounts}</td>
          </tr>
        ))}
      </tbody>
    </table>
    </div>
  </>
);

// One conversation, opened from its row: answers, consents and the chat.
const Conversation = ({ id, steps }) => {
  const [c, setC] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const token = await getToken();
        const res = await fetch(`${API}/admin/meet-analytics/conversations/${id}`, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        setC(await res.json());
      } catch (err) {
        setError(`Couldn't load this conversation (${err.message})`);
      }
    })();
  }, [id]);

  if (error) return <div className="as-error">{error}</div>;
  if (!c) return <div className="as-loading">Loading…</div>;

  const reached = steps.findIndex(s => s.key === c.step);
  const a = c.answers || {};
  const profile = Object.entries(a.profile || {}).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`);
  const facts = [
    a.firstName && `Name: ${a.firstName}`,
    ...profile,
    a.lookingFor && `Looking for: ${a.lookingFor}`,
    a.personality && `Personalities: ${a.personality}`,
    a.ageRange && `Age range: ${a.ageRange}`,
    a.age && `Age: ${a.age}`,
    a.gender && `Gender: ${a.gender}`,
    a.location && `Based: ${a.location}`,
    c.email && `Email: ${c.email}`,
    c.consents?.termsAcceptedAt && `Accepted terms ${fmtTime(c.consents.termsAcceptedAt)}`,
    c.consents?.deleteIfInactive && 'Chose: delete after 3 months if no suggestions interest them',
    c.userId && `Account: ${c.userId}`
  ].filter(Boolean);

  return (
    <div className="as-pa-timeline">
      <div style={{ marginBottom: '0.6rem' }}>
        {steps.map((s, i) => (
          <span key={s.key} className={`as-pa-tag${i <= reached ? ' as-pa-tag--good' : ''}`} style={{ marginRight: 4 }}>
            {i <= reached ? '✓' : '·'} {s.label}
          </span>
        ))}
      </div>
      {c.homePageVisits?.length > 0 && (
        <div style={{ marginBottom: '0.6rem' }}>
          <strong>Community home page visits by this visitor ({c.homePageVisits.length})</strong>
          {c.homePageVisits.map((v, i) => (
            <div key={i}>
              {fmtTime(v.at)} — {v.community} · {fmtDuration(v.activeMs)} · scrolled {v.maxScroll}% · {v.source}
              {new Date(v.at) < new Date(c.messages[0]?.timestamp || 0) ? '' : ' (after this chat)'}
            </div>
          ))}
        </div>
      )}
      {facts.length > 0 && (
        <div style={{ marginBottom: '0.6rem' }}>
          {facts.map((f, i) => <div key={i}>{f}</div>)}
        </div>
      )}
      {c.messages.map((m, i) => (
        <div key={i} style={{ margin: '0.3rem 0', paddingLeft: m.role === 'user' ? '1.5rem' : 0 }}>
          <strong>{m.role === 'user' ? 'Visitor' : 'Bot'}</strong>
          {m.timestamp && <span style={{ color: '#94a3b8' }}> {new Date(m.timestamp).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</span>}
          {m.content && <div style={{ whiteSpace: 'pre-wrap' }}>{m.content}</div>}
          {m.suggestions?.length > 0 && (
            <div style={{ color: '#475569' }}>
              {m.suggestions.map((p, j) => (
                <div key={j}>· {p.name}{p.ageRange ? `, ${p.ageRange}` : ''}{p.location ? `, ${p.location}` : ''}{m.examples ? ' (example)' : ''} — {p.bio}</div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

const RANGES = [1, 7, 14, 30, 90];

const MeetAnalyticsSection = ({ days: pageDays, communityId, includeInternal }) => {
  // Its own date range, starting from (and following) the page's one above.
  const [days, setDays] = useState(pageDays);
  useEffect(() => { setDays(pageDays); }, [pageDays]);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(null);
  const [more, setMore] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  // '' = both versions, '1' = v1 only, '2' = v2 (buttons) only.
  const [variant, setVariant] = useState('');

  const query = () => new URLSearchParams({ days, ...(communityId ? { communityId } : {}), ...(includeInternal ? { includeInternal: '1' } : {}), ...(variant ? { variant } : {}) });

  const load = async () => {
    setError('');
    try {
      const token = await getToken();
      const res = await fetch(`${API}/admin/meet-analytics?${query()}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setData(json);
      setMore([]);
      setHasMore(json.totalConversations > json.recent.length);
    } catch (err) {
      setError(`Couldn't load /meet analytics (${err.message})`);
    }
  };

  useEffect(() => { load(); }, [days, communityId, includeInternal, variant]); // eslint-disable-line react-hooks/exhaustive-deps

  const conversations = data ? [...data.recent, ...more] : [];

  const loadMore = async () => {
    const last = conversations[conversations.length - 1];
    if (!last) return;
    setLoadingMore(true);
    try {
      const token = await getToken();
      const qs = query();
      qs.set('before', last.createdAt);
      const res = await fetch(`${API}/admin/meet-analytics/conversations?${qs}`, { headers: { Authorization: `Bearer ${token}` } });
      const rows = await res.json();
      setMore(prev => [...prev, ...rows]);
      setHasMore(rows.length >= 50);
    } finally {
      setLoadingMore(false);
    }
  };

  const s = data?.summary;
  const landed = data?.funnel[0]?.count || 0;
  const stepLabel = (key) => data?.steps.find(x => x.key === key)?.label || key;

  return (
    <div style={{ marginTop: '2.5rem', paddingTop: '1.5rem', borderTop: '2px solid #e2e8f0' }}>
      <h2 className="as-section-title">AI chat pages (/meet)</h2>
      <p className="as-pa-hint">
        Visitors to the chatbot landing pages (e.g. /meet/UK_Bikers, ?v=2 for the button version). Uses the community and
        &quot;Include admin &amp; localhost&quot; filters above (the latter also shows local test chats); pick its own date range here.
      </p>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', margin: '0.5rem 0 0.4rem' }}>
        {RANGES.map(d => (
          <button key={d} className={`as-filter-tab ${days === d ? 'as-filter-tab--active' : ''}`} onClick={() => setDays(d)}>{d}d</button>
        ))}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', margin: '0 0 1rem' }}>
        {[['', 'v1 + v2'], ['1', 'v1 only'], ['2', 'v2 (buttons) only']].map(([v, label]) => (
          <button key={v || 'all'} className={`as-filter-tab ${variant === v ? 'as-filter-tab--active' : ''}`} onClick={() => setVariant(v)}>{label}</button>
        ))}
      </div>
      {error && <div className="as-error">{error}</div>}
      {!data && !error && <div className="as-loading">Loading…</div>}

      {data && (<>
        <div className="as-pa-tiles">
          <StatTile label="Chats started" value={s.conversations} sub={`${s.explained} asked how it works`} />
          <StatTile label="Replied" value={`${Math.round((s.chatted / (s.conversations || 1)) * 100)}%`} sub={`${s.chatted} chats`} />
          <StatTile label="Saw people" value={s.sawPeople} sub={`${s.sawExamples} saw example profiles`} />
          <StatTile label="Gave email" value={s.gaveEmail} sub={`${s.askedMoreTypes} asked for other types`} />
          <StatTile label="Accounts created" value={s.accounts} sub={`${s.accountLater} said "not right now"`} />
          <StatTile label="Typical chat" value={`${s.medianMessages} replies`} sub={`median ${fmtDuration(s.medianDurationMs)}`} />
        </div>

        <div className="as-pa-split">
          <div className="as-pa-card">
            <h2 className="as-section-title">How far they got</h2>
            <p className="as-pa-hint">Chats reaching each step (and % of everyone who landed).</p>
            {data.funnel.map(f => (
              <div key={f.key} className="as-pa-hbar-row">
                <div className="as-pa-hbar-label">{f.label}</div>
                <div className="as-pa-hbar-track">
                  <div className="as-pa-hbar" style={{ width: `${landed ? (f.count / landed) * 100 : 0}%` }} />
                </div>
                <div className="as-pa-hbar-value">{f.count} <span>({landed ? Math.round((f.count / landed) * 100) : 0}%)</span></div>
              </div>
            ))}
          </div>

          <div className="as-pa-card">
            <h2 className="as-section-title">Where they stopped</h2>
            <table className="as-act-table">
              <tbody>
                {data.dropOff.length === 0 && <tr><td className="as-act-empty">—</td></tr>}
                {data.dropOff.map(d => <tr key={d.key}><td>{d.label}</td><td className="as-pa-num">{d.count}</td></tr>)}
              </tbody>
            </table>
            {!variant && <GroupTable title="v1 vs v2" rows={data.byVariant} />}
          </div>
        </div>

        <div className="as-pa-split">
          {!communityId && <div className="as-pa-card"><GroupTable title="By community" rows={data.byCommunity} /></div>}
          <div className="as-pa-card"><GroupTable title="By source (utm / referrer)" rows={data.bySource} /></div>
        </div>

        <h2 className="as-section-title" style={{ marginTop: '1.5rem' }}>
          Conversations ({conversations.length} of {data.totalConversations})
        </h2>
        <div className="as-act-table-wrap">
          <table className="as-act-table">
            <thead><tr><th>Time</th><th>Community</th><th>Visitor</th><th>Version</th><th>From</th><th>Replies</th><th>Length</th><th>Got as far as</th></tr></thead>
            <tbody>
              {conversations.length === 0 && <tr><td colSpan="8" className="as-act-empty">None</td></tr>}
              {conversations.map(c => {
                const isOpen = open === c._id;
                return (
                  <React.Fragment key={c._id}>
                    <tr className="as-pa-click-row" onClick={() => setOpen(isOpen ? null : c._id)}>
                      <td className="as-act-time">{fmtTime(c.createdAt)}</td>
                      <td>{c.communityTitle}</td>
                      <td className="as-act-who">
                        {c.visitorId ? c.visitorId.slice(0, 8) : '?'} · {c.device || '?'}
                        {c.returning && <span className="as-pa-tag">returning</span>}
                        {c.homePageVisits > 0 && (
                          <span className="as-pa-tag" title={`${c.homePageVisitsBefore} before this chat`}>
                            home page ×{c.homePageVisits}
                          </span>
                        )}
                        {c.local && <span className="as-pa-tag">local</span>}
                      </td>
                      <td>{c.variant === 2 ? 'v2' : 'v1'}</td>
                      <td>{c.source}</td>
                      <td className="as-pa-num">{c.userMessages}</td>
                      <td className="as-pa-num">{fmtDuration(c.durationMs)}</td>
                      <td>
                        {stepLabel(c.step)}
                        {c.step === 'account_created' && <span className="as-pa-tag as-pa-tag--good">✓ account</span>}
                        {c.hasEmail && c.step !== 'account_created' && <span className="as-pa-tag">email</span>}
                        <span style={{ color: '#6366f1', fontSize: '0.75rem' }}> {isOpen ? '▲' : '▼'}</span>
                      </td>
                    </tr>
                    {isOpen && (
                      <tr><td colSpan="8"><Conversation id={c._id} steps={data.steps} /></td></tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        {hasMore && conversations.length > 0 && (
          <div style={{ textAlign: 'center', margin: '0.75rem 0 1.5rem' }}>
            <button className="as-filter-tab" onClick={loadMore} disabled={loadingMore}>
              {loadingMore ? 'Loading…' : 'Show more'}
            </button>
          </div>
        )}
      </>)}
    </div>
  );
};

export default MeetAnalyticsSection;
